import React, { useRef } from 'react';
import { Button, Checkbox, Dropdown, Form, Header, Icon, Input, Message, Segment } from 'semantic-ui-react';
import TierLabel from '../subcomponents/TierLabel';
import { RECOMMENDATION_TIERS } from '../constants';
import { MERGE_FIELDS, TEXT_CHARACTER_LIMIT, getTextSegmentCount } from './messagingUtils';

// Inserts merge fields where the cursor was last placed, falling back to the end
const MessageBody = ({ value, onChange, rows = 3, disabled, error, footer }) => {
    const cursor = useRef(null);
    const rememberCursor = (e) => { cursor.current = e.target.selectionStart; };

    const insertField = (_, { value: field }) => {
        const position = cursor.current ?? value.length;
        onChange(`${value.slice(0, position)}${field}${value.slice(position)}`);
        cursor.current = position + field.length;
    }

    return (
        <Form.Field error={!!error}>
            <textarea
                rows={rows}
                value={value}
                disabled={disabled}
                onChange={(e) => { rememberCursor(e); onChange(e.target.value); }}
                onSelect={rememberCursor}
                onClick={rememberCursor}
                onKeyUp={rememberCursor}
            />
            <div className='Onboarding__message-footer'>
                <Dropdown
                    className='Onboarding__merge-fields'
                    text='Insert Field'
                    icon='plus'
                    floating
                    labeled
                    button
                    scrolling
                    disabled={disabled}
                    selectOnBlur={false}
                    value={null}
                    options={MERGE_FIELDS}
                    onChange={insertField}
                />
                {footer}
            </div>
            {error && <div className='Onboarding__field-error'>{error}</div>}
        </Form.Field>
    );
}

const TemplateCard = ({ template, errors = {}, onChange, onRemove, children }) => (
    <Segment className={`Onboarding__recommendation ${template.selected ? 'Onboarding__recommendation--selected' : ''}`}>
        <div className='Onboarding__field-row'>
            <Checkbox checked={template.selected} onChange={() => onChange({ selected: !template.selected })} aria-label={`Include ${template.name}`} />
            <Form.Field error={!!errors.name} className='Onboarding__field-label'>
                <Input size='small' placeholder='Template name' value={template.name} disabled={!template.selected} onChange={(_, { value }) => onChange({ name: value })} />
                {errors.name && <div className='Onboarding__field-error'>{errors.name}</div>}
            </Form.Field>
            {onRemove && <Icon name='trash alternate outline' link color='red' onClick={onRemove} aria-label='Remove template' />}
        </div>
        {template.purpose && <p className='Onboarding__recommendation-summary'>{template.purpose}</p>}
        {template.selected && <div className='Onboarding__template-body'>{children}</div>}
    </Segment>
);

const MessageTemplates = ({
    textTemplates,
    emailTemplates,
    errors,
    explanation,
    isStale,
    onRegenerate,
    onChangeText,
    onChangeEmail,
    onAddText,
    onAddEmail,
    onRemoveText,
    onRemoveEmail,
    canContinue,
    onBack,
    onContinue
}) => (
    <Segment>
        <Header as='h3'>
            Message Templates
            <Header.Subheader>{explanation} Edit anything; templates are saved to your Library at launch.</Header.Subheader>
        </Header>
        {isStale && (
            <Message warning size='small'>
                Your business profile changed since these drafts were written.
                <Button size='tiny' basic compact style={{ marginLeft: '1em' }} onClick={onRegenerate}>Redraft Templates</Button>
            </Message>
        )}
        <Form noValidate>
            <Header as='h4'>
                Text Messages <TierLabel tier={RECOMMENDATION_TIERS.REQUIRES_ACTIVATION} />
                <Header.Subheader>Texting requires carrier (10DLC) registration before messages can be sent.</Header.Subheader>
            </Header>
            {textTemplates.map(template => {
                const segments = getTextSegmentCount(template.message);
                return (
                    <TemplateCard
                        key={template.id}
                        template={template}
                        errors={errors.texts?.[template.id]}
                        onChange={(changes) => onChangeText(template.id, changes)}
                        onRemove={template.is_custom ? () => onRemoveText(template.id) : null}
                    >
                        <MessageBody
                            value={template.message}
                            onChange={(message) => onChangeText(template.id, { message })}
                            error={errors.texts?.[template.id]?.message}
                            footer={(
                                <span className={`Onboarding__char-count ${segments > 1 ? 'Onboarding__char-count--over' : ''}`}>
                                    {template.message.length}/{TEXT_CHARACTER_LIMIT}{segments > 1 && ` · sends as ${segments} messages (additional charges apply)`}
                                </span>
                            )}
                        />
                    </TemplateCard>
                );
            })}
            <Button basic size='small' type='button' onClick={onAddText}><Icon name='plus' /> Add Text Template</Button>

            <Header as='h4'>
                Emails <TierLabel tier={RECOMMENDATION_TIERS.RECOMMENDED} />
            </Header>
            {emailTemplates.map(template => (
                <TemplateCard
                    key={template.id}
                    template={template}
                    errors={errors.emails?.[template.id]}
                    onChange={(changes) => onChangeEmail(template.id, changes)}
                    onRemove={template.is_custom ? () => onRemoveEmail(template.id) : null}
                >
                    <Form.Field error={!!errors.emails?.[template.id]?.subject}>
                        <Input size='small' fluid label='Subject' value={template.subject} onChange={(_, { value }) => onChangeEmail(template.id, { subject: value })} />
                        {errors.emails?.[template.id]?.subject && <div className='Onboarding__field-error'>{errors.emails[template.id].subject}</div>}
                    </Form.Field>
                    <MessageBody
                        rows={7}
                        value={template.body}
                        onChange={(body) => onChangeEmail(template.id, { body })}
                        error={errors.emails?.[template.id]?.body}
                    />
                </TemplateCard>
            ))}
            <Button basic size='small' type='button' onClick={onAddEmail}><Icon name='plus' /> Add Email Template</Button>
        </Form>
        <div className='Onboarding__footer'>
            <Button basic type='button' onClick={onBack}><Icon name='arrow left' /> Back</Button>
            <Button primary floated='right' type='button' disabled={!canContinue} onClick={onContinue}>
                Continue to Follow-Up Sequence <Icon name='arrow right' />
            </Button>
            <div style={{ clear: 'both' }} />
        </div>
    </Segment>
);

export default MessageTemplates;
